<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>

    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-2 mb-2 mb-xl-0 font-weight-bold">邀友入會活動管理</h4>
        <div
          class="
            col-12 col-xl-8
            d-flex
            flex-column flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            v-if="checkPermission([consts.MGM_CAMPAIGN_CREATE ]) && !!this.query.merchant_id"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            :to="{ name: 'MgmCampaignCreate', params: { merchantId: this.query.merchant_id }  }"
          >
            <i class="fa fa-plus"></i>
            新增邀友入會活動
          </b-button>
          <b-form-select
            v-model="query.merchant_id"
            :options="merchants"
            @change="handlePageReset"
            :disabled="merchants.length == 2"
          ></b-form-select>
          <b-form-select
            class="ml-2"
            v-model="query.is_processing"
            :options="isProcessingOptions"
            @change="handlePageReset"
          ></b-form-select>
          <b-form-select
            class="ml-2"
            v-model="query.status"
            :options="statusOptions"
            @change="handlePageReset"
          ></b-form-select>

          <b-input-group class="ml-0 ml-xl-4">
            <b-form-input v-model="query.keyword" placeholder="搜尋" v-on:keyup.enter="handlePageReset"></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageReset">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table striped hover responsive :items="mgmCampaigns" :fields="fields" @sort-changed="handleSort">

                <template #cell(start_at)="data">
                  <div>
                    {{ dateFormatter(data.item.start_at) }}
                  </div>
                </template>

                <template #cell(end_at)="data">
                  <div>
                    {{ dateFormatter(data.item.end_at) }}
                  </div>
                </template>

                <template #cell(is_processing)="data">
                  <b-badge :variant="data.item.is_processing ? 'success' : 'danger'">
                    {{ data.item.is_processing ? '生效中' : '已失效' }}
                  </b-badge>
                </template>

                <template #cell(status)="data">
                  <b-badge v-if="data.item.status == 'draft'" variant="warning">
                    草稿
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'published'"
                    variant="success"
                  >
                    發布
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'closed'"
                    variant="danger"
                  >
                    關閉
                  </b-badge>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.MGM_CAMPAIGN_LIST_VIEW])"
                    variant="inverse-primary"
                    :to="{ name: 'MgmCampaignView', params: { id: data.item.id } }"
                  >
                    <span class="mdi mdi-eye"></span> 查看
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.MGM_CAMPAIGN_MODIFY])"
                    variant="inverse-warning"
                    :to="{ name: 'MgmCampaignEdit', params: { id: data.item.id } }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯
                  </b-button>
                  <span 
                    v-if="checkPermission([consts.MGM_CAMPAIGN_DELETE])"
                    :id="`delete-btn-wrapper-${data.item.id}`"
                    style="display: inline-block;"
                  >
                    <b-button
                      variant="inverse-danger"
                      class="icon-btn"
                      :disabled="data.item.status != 'closed'"
                      @click="deleteForm(data.item)"
                    >
                      <span class="mdi mdi-delete"></span> 刪除
                    </b-button>
                  </span>
                  <b-tooltip 
                    v-if="data.item.status != 'closed'"
                    :target="`delete-btn-wrapper-${data.item.id}`" 
                    triggers="hover"
                  >
                    只能刪除已關閉的活動
                  </b-tooltip>
                </template>

              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>

  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import mgmCampaignApi from "@/apis/mgmCampaign";
import { mapState } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import generateLiffUrl from "@/mixins/Dashboard/liffUrlHelper";
import merchantApi from "@/apis/merchant";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    CustomPagination,
    BreadCrumb
  },

  mixins: [
    paginationMixin, 
    generateLiffUrl
  ],

  data() {
    return {
      consts: consts,
      showLoading: false,
      mgmCampaigns: [],
      merchantId: null,
      merchants: [{ value: null, text: "請選擇" }],
      statusOptions: [
        { value: null, text: "選擇發布狀態" },
        { value: "published", text: "發布" },
        { value: "draft", text: "草稿" },
        { value: "closed", text: "關閉" },
      ],
      isProcessingOptions: [
        { text: "選擇生效狀態", value: 2 },
        { text: "生效中", value: 1 },
        { text: "已失效", value: 0 },
      ],
      fields: [
        { key: "name", label: "邀友入會活動名稱", sortable: true },
        {
          key: "is_processing",
          label: "生效狀態",
          formatter: (value, key, item) => {
            const now = new Date();
            const startAt = new Date(item.start_at);
            const endAt = new Date(item.end_at);

            item.is_processing = startAt <= now &&
              endAt >= now &&
              item.status === 'published';

            return item.is_processing;
          }
        },
        { key: "status", label: "發布狀態", sortable: true },
        { key: "start_at", label: "活動開始時間", sortable: true },
        { key: "end_at", label: "活動結束時間", sortable: true },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      initialized: false,
      query: {
        keyword: "",
        per_page: 10,
        page: 1,
        sort_by: "updated_at",
        order_by: "desc",
        status: null,
        is_processing: 2,
        merchant_id: null,
      },
    };
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },

  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      const defaultQuery = {
        is_processing: 1,
      };
      this.query = { ...defaultQuery, ...to.query };
      this.fetchMgmCampaigns();
    },
  },

  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },

  async mounted() {
    await this.fetchMerchants(); // will fetch campaigns if merchant_id is set
    this.initialized = true;
  },

  methods: {
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },

    async fetchMerchants() {
      const { data } = await merchantApi.list();

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: `${merchant.name} (${merchant.type_name})`,
          };
        }),
      ];

      if (!this.query.merchant_id) {
        let defaultMerchant;
        if (data.data.length === 1) {
          defaultMerchant = data.data[0];
        } else {
          defaultMerchant = data.data
            .filter((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE)
            .shift();
        }

        if (defaultMerchant) {
          this.query.merchant_id = defaultMerchant.id;
        }
      }

      if (this.query.merchant_id) {
        var hasMerchant = this.merchants.find((item) => {
          return item.value === this.merchantId;
        });

        if (!hasMerchant) {
          this.$swal.fire({
            type: "warning",
            title: "查無此通路",
            confirmButtonText: "確定",
            confirmButtonColor: "#d33",
            reverseButtons: true,
          });
          return;
        }
        await this.fetchMgmCampaigns();
      }
    },

    async fetchMgmCampaigns() {
      this.showLoading = true;
      try {
        const params = { ...this.query };
        let response = await mgmCampaignApi.getMgmCampaigns(this.query.merchant_id, params);
        this.mgmCampaigns = response.data.data;
        this.totalRows = response.data.meta.total;
        this.showLoading = false;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取表單列表失敗", "error");
      }
    },

    async deleteForm(form) {
      try {
        this.showLoading = true;
        const result = await this.$swal({
          title: "你確定要刪除嗎？",
          html: `<br/>刪除 ${form.name}<br/><br/>刪除後，相關資料將無法回復，<br/>請您確認！<br/>`,
          type: "warning",
          showConfirmButton: true,
          showCancelButton: true,
          cancelButtonColor: "#3085D7",
          cancelButtonText: "不刪除",
          confirmButtonColor: "#DD3333",
          confirmButtonText: "確定刪除",
          reverseButtons: true,
        });

        if (!result.value) {
          return;
        }

        await mgmCampaignApi.deleteMgmCampaign(form.id);
        this.$swal.fire({
          type: "success",
          title: "刪除成功",
          confirmButtonText: "確定",
        });
        await this.fetchMgmCampaigns();
      } catch (error) {
        console.error(error);

        // 檢查是否為 403 錯誤且訊息包含 "This action is unauthorized"
        const errorTitle = (error.response?.status === 403 && 
            error.response?.data?.message?.includes("Only closed status can be deleted")) 
        ? "只能刪除已關閉的活動" 
        : "刪除失敗";
        
        this.$swal.fire({
          title: errorTitle,
          type: "error",
        });
      } finally {
        this.showLoading = false;
      }
    },

    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },

    dateFormatter: (value) => {
      if (!value) return "無";
      return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
    },
  },
};
</script>

<style scoped lang="scss">
.tooltip {
  opacity: 1;
}
</style>